import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";

/**
 * Generic searchable combobox built on top of shadcn Command + Popover.
 * Accepts one or more sections of { heading, options: [{value, label}] }.
 *
 * Props
 *  - value:        selected value string
 *  - onChange:     (value) => void
 *  - sections:     Array<{ heading?: string, options: Array<{value, label}> }>
 *  - placeholder:  string shown when nothing selected
 *  - searchPlaceholder: placeholder for the search input
 *  - emptyLabel:   copy when no match
 *  - testid:       root data-testid
 *  - className:    className for the trigger button
 */
export function Combobox({
    value,
    onChange,
    sections = [],
    placeholder = "Select…",
    searchPlaceholder = "Search…",
    emptyLabel = "Nothing found.",
    testid,
    className,
    disabled = false,
}) {
    const [open, setOpen] = React.useState(false);

    const selectedLabel = React.useMemo(() => {
        for (const s of sections) {
            const hit = s.options.find((o) => o.value === value);
            if (hit) return hit.label;
        }
        return value || "";
    }, [value, sections]);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    disabled={disabled}
                    className={cn(
                        "w-full justify-between font-normal text-left h-auto min-h-[42px] px-3 py-2",
                        !value && "text-mm-muted",
                        className,
                    )}
                    data-testid={testid}
                >
                    <span className="truncate">{selectedLabel || placeholder}</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className="w-[--radix-popover-trigger-width] p-0 bg-white opacity-100 shadow-2xl"
                align="start"
                data-testid={testid ? `${testid}-panel` : undefined}
            >
                <Command shouldFilter>
                    <CommandInput
                        placeholder={searchPlaceholder}
                        data-testid={testid ? `${testid}-search` : undefined}
                    />
                    <CommandList className="max-h-72">
                        <CommandEmpty>{emptyLabel}</CommandEmpty>
                        {sections.map((sec, idx) => (
                            <CommandGroup key={idx} heading={sec.heading}>
                                {sec.options.map((opt) => (
                                    <CommandItem
                                        key={opt.value}
                                        value={`${opt.label} ${opt.value}`}
                                        onSelect={() => {
                                            onChange(opt.value);
                                            setOpen(false);
                                        }}
                                        data-testid={
                                            testid
                                                ? `${testid}-opt-${opt.value.replace(/[^a-zA-Z0-9]/g, "-")}`
                                                : undefined
                                        }
                                    >
                                        <Check
                                            className={cn(
                                                "mr-2 h-4 w-4",
                                                value === opt.value ? "opacity-100" : "opacity-0",
                                            )}
                                        />
                                        <span className="flex-1 truncate">{opt.label}</span>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        ))}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

export default Combobox;
